import React, { useState } from 'react';
import { X, Pencil } from 'lucide-react';

const editableFields = [
  { value: 'status',        label: 'Status' },
  { value: 'priority',      label: 'Priority' },
  { value: 'assignedTo',    label: 'Assigned To' },
  { value: 'type',          label: 'Type' },
  { value: 'linkedContact', label: 'Linked Contact' },
  { value: 'linkedAccount', label: 'Linked Account' },
];

const fieldValueOptions = {
  status:   ['PENDING', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'],
  priority: ['HIGH', 'MEDIUM', 'LOW'],
  type:     ['CALL', 'EMAIL', 'MEETING', 'FOLLOW_UP', 'DEMO', 'OTHER'],
};

const inputCls = "w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white";

const TaskBulkEditModal = ({ count, onClose, onConfirm }) => {
  const [fieldName, setFieldName] = useState('');
  const [fieldValue, setFieldValue] = useState('');

  const hasDropdown = fieldName && fieldValueOptions[fieldName];

  const handleFieldChange = (e) => {
    setFieldName(e.target.value);
    setFieldValue(''); // reset value when field changes
  };

  const handleConfirm = () => {
    if (!fieldName || !fieldValue.trim()) return;
    onConfirm(fieldName, fieldValue.trim());
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900">Bulk Edit Tasks</h3>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-gray-100 text-gray-400 transition-colors">
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-5">
          <div className="flex items-center gap-2 mb-4 text-sm text-gray-600">
            <Pencil size={14} className="text-yellow-500" />
            Editing <span className="font-semibold text-gray-900 mx-1">{count}</span> task{count > 1 ? 's' : ''}
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Field to Edit</label>
              <select value={fieldName} onChange={handleFieldChange} className={inputCls}>
                <option value="">Select field</option>
                {editableFields.map(f => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>

            {fieldName && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">New Value</label>
                {hasDropdown ? (
                  <select value={fieldValue} onChange={(e) => setFieldValue(e.target.value)} className={inputCls}>
                    <option value="">Select value</option>
                    {fieldValueOptions[fieldName].map(v => (
                      <option key={v} value={v}>{v.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={fieldValue}
                    onChange={(e) => setFieldValue(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleConfirm()}
                    placeholder={`Enter new ${editableFields.find(f => f.value === fieldName)?.label}`}
                    autoFocus
                    className={inputCls}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors font-medium text-gray-600"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!fieldName || !fieldValue.trim()}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default TaskBulkEditModal;